import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { buildPermission } from "portal/utils/auth";
import {
  BUTTON,
  classes,
  GREEN_BUTTON,
  RED_BUTTON,
  WHITE_BUTTON,
  YELLOW_BUTTON,
} from "portal/theme/theme";
import { capitalize } from "portal/utils/strings";
import { ConfiguredJob, rtcJobsApi } from "common/state/rtcJobsApi";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { farmApi } from "common/state/portalApi/farm";
import { FullViewportPage } from "portal/components/Page";
import { getJobOrchestratePath, Path } from "portal/utils/routing";
import { Header } from "portal/components/header/Header";
import { jobExplorer } from "common/state/jobExplorer";
import {
  jobIsCancellable,
  jobIsPauseable,
  jobIsStartable,
} from "portal/utils/jobs";
import { JobNav } from "./JobNav";
import { JobType } from "protos/atk/job_type";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ObjectiveAssignment, State } from "protos/rtc/jobs";
import { ObjectiveReorderList } from "./ObjectiveReorderList";
import { OrchestrationMap } from "./orchestrate/OrchestrationMap";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { RigCompatibilityStatus } from "protos/portal/atk_equipment";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { RobotSelectionList } from "./RobotSelectionList";
import { skipToken } from "@reduxjs/toolkit/query";
import { SummaryCardItem } from "portal/components/SummaryCardItem";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useCloneJobDialog } from "./CloneJobDialog";
import {
  useCustomerStatus,
  useFarmStatus,
  useJobCreatorStatus,
  useJobStateStatus,
  useJobTimesStatus,
  useJobTypeStatus,
  useRigStatus,
} from "./JobStatus";
import {
  useDeviceLocations,
  useTractorLocations,
} from "common/hooks/useLiveRtcLocation";
import { useDispatch } from "react-redux";
import { useEditJobDialog } from "./EditJobForm";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EditIcon from "@mui/icons-material/Edit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import React, { FC, useEffect, useState } from "react";
import StopIcon from "@mui/icons-material/Stop";

export const JobDetailsPage: FC = () => {
  const { t } = useTranslation();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    data: configuredJob,
    isLoading,
    isSuccess,
    isError,
  } = useQueryPopups(
    rtcJobsApi.endpoints.getConfiguredJob.useQuery(
      jobId ? { id: jobId } : skipToken
    )
  );

  const { data: rigResp } = atkEquipmentApi.endpoints.getRig.useQuery(
    { id: configuredJob?.groundPrep?.rigId ?? "", validate: true },
    { skip: !configuredJob?.groundPrep?.rigId }
  );
  const rig = rigResp?.validationResult?.effectiveConfig;
  const dispatch = useDispatch();
  const { data: farm } = useQueryPopups(
    farmApi.endpoints.getFarm.useQuery(
      configuredJob ? configuredJob.job.farmId : skipToken
    )
  );
  useEffect(() => {
    dispatch(jobExplorer.actions.setFarm(farm));
  }, [farm, dispatch]);

  // todo: re-evaluate if we move to specifying co-located zones
  const field = farm?.zones.find(
    (z) => z.id?.id === configuredJob?.job.fieldId
  );
  const endpointIds =
    field?.contents?.field?.centerPivotIds.flatMap(
      (i) =>
        farm?.zones
          .find((z) => z.id?.id === i.id)
          ?.contents?.centerPivot?.legs.flatMap((l) => l.endpointDeviceId) ?? []
    ) ?? [];

  useTractorLocations(
    configuredJob?.job.robotWhitelist?.entries.map((a) => a.robotSerial) ?? []
  );
  useDeviceLocations(endpointIds);

  const { data: assignments } = useQueryPopups(
    rtcJobsApi.endpoints.listAssignments.useQuery(
      {
        objectiveIds: configuredJob?.job.objectives.map((o) => `${o.id}`),
      },
      { skip: !configuredJob?.job.objectives }
    )
  );

  useEffect(() => {
    const assignmentsByTractorId: Record<string, ObjectiveAssignment[]> = {};

    for (const assignment of assignments?.assignments ?? []) {
      const currentList = assignmentsByTractorId[assignment.robotSerial];
      if (currentList) {
        assignmentsByTractorId[assignment.robotSerial]?.push(assignment);
      } else {
        assignmentsByTractorId[assignment.robotSerial] = [assignment];
      }
    }

    dispatch(
      jobExplorer.actions.setAssignmentsByTractorSerial(assignmentsByTractorId)
    );
  }, [assignments, dispatch]);
  const parentLink = searchParams.get("returnTo") ?? Path.JOBS;

  if (isError || !jobId) {
    return <Navigate to={parentLink} />;
  }

  return (
    <>
      <Header
        title={capitalize(t("models.jobs.job_one"))}
        pageTitle={`${
          configuredJob?.job.name ?? t("utils.descriptors.loading")
        } - ${capitalize(t("models.jobs.job_one"))}`}
        parentLink={parentLink}
      >
        <JobNav jobId={jobId} />
      </Header>
      <FullViewportPage maxWidth={false}>
        <div className="pt-20 pb-10 flex flex-col h-full">
          {isLoading && <CircularProgress variant="indeterminate" />}
          {isSuccess && <JobView configuredJob={configuredJob} rig={rig} />}
        </div>
      </FullViewportPage>
    </>
  );
};

interface JobViewProps {
  configuredJob: ConfiguredJob;
  rig: RigEffectiveConfig | undefined;
}

const JobView: FC<JobViewProps> = ({ configuredJob, rig }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canEditJob = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.autotractor_jobs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.autotractor_jobs,
        PermissionDomain.all
      ),
    ],
  });

  const isInternalAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.autotractor_jobs,
      PermissionDomain.all
    )
  );

  const { renderDialog: renderEditJobDialog, open: openEditJobDialog } =
    useEditJobDialog(configuredJob.job);

  const { renderDialog: renderCloneJobDialog, open: openCloneJobDialog } =
    useCloneJobDialog(configuredJob.job);

  const summaryCardItems = [
    ...useCustomerStatus(configuredJob.job),
    ...useJobCreatorStatus(configuredJob.job),
    ...useFarmStatus(
      configuredJob.job,
      canEditJob ? openEditJobDialog : undefined
    ),
    ...useJobTypeStatus(
      configuredJob.job,
      canEditJob ? openEditJobDialog : undefined
    ),
    ...useRigStatus(configuredJob),
    ...useJobStateStatus(configuredJob.job),
    ...useJobTimesStatus(configuredJob.job),
  ];

  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [changeJobState] = useMutationPopups(
    rtcJobsApi.endpoints.changeJobState.useMutation()
  );

  function startJob(): void {
    // the start button is overloaded between readying a pending job and
    // resuming a paused job; figure out which one is appropriate to do
    if (configuredJob.job.state === State.PAUSED) {
      changeJobState({ id: configuredJob.job.id, state: State.IN_PROGRESS });
    } else if (configuredJob.job.state === State.PENDING) {
      changeJobState({ id: configuredJob.job.id, state: State.READY });
    } else {
      console.error(
        `job started in unexpected state ${configuredJob.job.state}`
      );
    }
  }

  const [deleteJob] = useMutationPopups(
    rtcJobsApi.endpoints.deleteJob.useMutation()
  );

  const jobIsOrchestratable =
    configuredJob.job.state === State.READY ||
    configuredJob.job.state === State.IN_PROGRESS;

  const editButtonStyle = {
    ...BUTTON,
    className: classes(BUTTON.className, "ml-4"),
  };

  const deleteButtonStyle = {
    ...RED_BUTTON,
    className: classes(RED_BUTTON.className, "w-1/3 mx-4 my-2 self-center"),
  };

  const orchestrateButtonStyle = {
    ...WHITE_BUTTON,
    className: classes(WHITE_BUTTON.className, "col-span-full"),
  };

  // FIXME: this is a terrible way of doing this but it suffices for now
  let taskDisplaySettings: {
    hideTasks: boolean | undefined;
    showAllTasks: boolean | undefined;
  } = { hideTasks: undefined, showAllTasks: undefined };

  switch (configuredJob.job.type) {
    case JobType.LASER_WEED:
    case JobType.IN_SEASON: {
      // don't show tasks
      taskDisplaySettings = { hideTasks: true, showAllTasks: undefined };
      break;
    }
    case JobType.GROUND_PREP:
    case JobType.TRANSPORT: {
      // show all tasks
      taskDisplaySettings = { hideTasks: undefined, showAllTasks: true };
      break;
    }
  }

  const { data: isCompatibleResponse } = useQueryPopups(
    atkEquipmentApi.endpoints.checkRigCompatibility.useQuery(
      {
        rigId: configuredJob.groundPrep?.rigId ?? "",
        snapshotId: configuredJob.groundPrep?.rigSnapshotId ?? "",
      },
      {
        skip:
          !configuredJob.groundPrep?.rigId ||
          !configuredJob.groundPrep.rigSnapshotId,
      }
    )
  );

  return (
    <div className="grid grid-cols-[1fr_3fr] grid-rows-[auto_minmax(0,1fr)] gap-y-3 w-full h-full">
      {canEditJob && renderEditJobDialog()}
      {canEditJob && renderCloneJobDialog()}
      {confirmCancel && (
        <ConfirmationDialog
          title={t("components.ConfirmationDialog.title")}
          description={t(
            "views.autotractor.jobDetails.cancelDialog.description"
          )}
          destructive
          yesText={t("utils.actions.cancelLong", {
            subject: t("models.jobs.job_one"),
          })}
          noText={t("utils.actions.leaveUnchanged")}
          onClose={() => setConfirmCancel(false)}
          onYes={() =>
            changeJobState({ id: configuredJob.job.id, state: State.CANCELLED })
          }
        />
      )}
      {confirmDelete && (
        <ConfirmationDialog
          title={t("components.ConfirmationDialog.title")}
          description={t("components.ConfirmationDialog.delete.description", {
            subject: capitalize(t("models.jobs.job_one")),
          })}
          destructive
          yesText={t("utils.actions.delete")}
          onClose={() => setConfirmDelete(false)}
          onYes={async () => {
            const result = await deleteJob({ id: configuredJob.job.id });
            if (result.error === undefined) {
              navigate(Path.JOBS);
            }
          }}
        />
      )}
      <Typography variant="h1" className="col-span-full text-4xl">
        {configuredJob.job.name}
        {canEditJob && (
          <Button
            {...editButtonStyle}
            startIcon={<EditIcon />}
            onClick={openEditJobDialog}
          >
            {t("utils.actions.editLong", { subject: t("models.jobs.job_one") })}
          </Button>
        )}
        {canEditJob && (
          <Button
            {...editButtonStyle}
            startIcon={<ContentCopyIcon />}
            onClick={openCloneJobDialog}
          >
            {t("views.autotractor.jobDetails.cloneJob")}
          </Button>
        )}
      </Typography>
      <Paper className="flex flex-col gap-2 pt-2 overflow-y-auto">
        <ul className="p-0 px-4 flex flex-col gap-2 mb-0">
          {summaryCardItems.map((props, index) => (
            <SummaryCardItem {...props} key={`summary-${index}`} />
          ))}
        </ul>
        {isCompatibleResponse &&
          isCompatibleResponse.status !==
            RigCompatibilityStatus.COMPATIBILITY_STATUS_OK && (
            <Alert severity="warning" className="text-wrap mx-4">
              {t("views.autotractor.jobDetails.equipmentSetupChangedWarning")}
            </Alert>
          )}
        {canEditJob && (
          <div className="grid grid-cols-3 gap-4 mx-4 my-2">
            <Button
              {...GREEN_BUTTON}
              disabled={!jobIsStartable(configuredJob.job)}
              startIcon={<PlayArrowIcon />}
              onClick={startJob}
            >
              {t("utils.actions.run")}
            </Button>
            <Button
              {...YELLOW_BUTTON}
              disabled={!jobIsPauseable(configuredJob.job)}
              startIcon={<PauseIcon />}
              onClick={() =>
                changeJobState({
                  id: configuredJob.job.id,
                  state: State.PAUSED,
                })
              }
            >
              {t("utils.actions.pause")}
            </Button>
            <Button
              {...RED_BUTTON}
              disabled={!jobIsCancellable(configuredJob.job)}
              startIcon={<StopIcon />}
              onClick={() => setConfirmCancel(true)}
            >
              {t("utils.actions.cancel")}
            </Button>
            {isInternalAuthorized && (
              <Button
                {...orchestrateButtonStyle}
                disabled={!jobIsOrchestratable}
                startIcon={<DashboardIcon />}
                onClick={() =>
                  navigate(getJobOrchestratePath(configuredJob.job.id))
                }
              >
                {t("views.autotractor.actions.orchestrateView")}
              </Button>
            )}
          </div>
        )}
        <div className="flex-1">
          <ObjectiveReorderList
            jobId={configuredJob.job.id}
            objectives={configuredJob.job.objectives}
          />
          <RobotSelectionList job={configuredJob.job} />
        </div>
        {canEditJob && (
          <Button {...deleteButtonStyle} onClick={() => setConfirmDelete(true)}>
            {t("utils.actions.deleteLong", {
              subject: t("models.jobs.job_one"),
            })}
          </Button>
        )}
      </Paper>
      <div className="flex h-full w-full">
        <OrchestrationMap
          configuredJob={configuredJob}
          objectives={configuredJob.job.objectives}
          {...taskDisplaySettings}
          rig={rig}
        />
      </div>
    </div>
  );
};
